<template>
    <nav
        class="site-navigation"
        data-test="site-navigation"
        :aria-label="navLabel">
        <div class="site-navigation__inner">
            <NavLogo v-bind="config.brand"></NavLogo>

            <div
                v-if="config.links && config.links.length"
                class="nav-links-wrapper">
                <NavLinks :links="config.links"></NavLinks>
            </div>

            <div class="site-navigation__right">
                <NavActions :actions="config.actions"></NavActions>

                <NavUserMenu
                    v-if="showDesktopUserMenu"
                    :config="config.userMenu!">
                </NavUserMenu>

                <NavAuthMenu
                    v-if="!isAuthenticated && config.signInMenu"
                    :config="config.signInMenu">
                </NavAuthMenu>

                <MobileCombinedMenu
                    v-if="showMobileCombinedMenu"
                    :links="config.links || []"
                    :user-menu="config.userMenu!"
                    :actions="config.actions">
                </MobileCombinedMenu>
            </div>
        </div>
    </nav>
</template>

<script lang="ts">
    import type { IDesktopUserInfoMenuConfig } from "@eg_ui/src/components/globals/DesktopUserInfoMenu/DesktopUserInfoMenu.vue";
    import type { IActionsConfig } from "@eg_ui/src/components/navigation/NavActions/NavActions.vue";
    import type { INavAuthMenuConfig } from "@eg_ui/src/components/navigation/NavAuthMenu/NavAuthMenu.vue";
    import type { INavLink } from "@eg_ui/src/components/navigation/NavLinks/NavLinks.vue";
    import type { INavLogoProps as IBrandConfig } from "@eg_ui/src/components/navigation/NavLogo/NavLogo.vue";

    export interface ISiteNavigationConfig {
        isAuthenticated: boolean;
        brand: IBrandConfig;
        links?: INavLink[] | null;
        actions: IActionsConfig;
        userMenu?: IDesktopUserInfoMenuConfig | null;
        signInMenu?: INavAuthMenuConfig | null;
    }

    export interface ISiteNavigationProps {
        config: ISiteNavigationConfig;
    }
</script>

<script setup lang="ts">
    import MobileCombinedMenu from "@eg_ui/src/components/globals/_mobile/MobileCombinedMenu/MobileCombinedMenu.vue";
    import NavActions from "@eg_ui/src/components/navigation/NavActions/NavActions.vue";
    import NavAuthMenu from "@eg_ui/src/components/navigation/NavAuthMenu/NavAuthMenu.vue";
    import NavLinks from "@eg_ui/src/components/navigation/NavLinks/NavLinks.vue";
    import NavLogo from "@eg_ui/src/components/navigation/NavLogo/NavLogo.vue";
    import NavUserMenu from "@eg_ui/src/components/navigation/NavUserMenu/NavUserMenu.vue";
    import { BREAKPOINT } from "@eg_ui/src/constants/breakpoints";
    import { injectI18n } from "@eg_ui/src/translations/i18n";
    import { useWindowSize } from "@vueuse/core";
    import { computed } from "vue";

    const { config } = defineProps<ISiteNavigationProps>();
    const { width } = useWindowSize();
    const intl = injectI18n();

    const isMobile = computed<boolean>(() => width.value < BREAKPOINT.MD);
    const isAuthenticated = computed<boolean>(() => config.isAuthenticated);

    const showMobileCombinedMenu = computed<boolean>(
        () => isMobile.value && isAuthenticated.value && !!config.userMenu,
    );
    const showDesktopUserMenu = computed<boolean>(() => !isMobile.value && isAuthenticated.value && !!config.userMenu);

    const navLabel = computed<string>(() => {
        return intl.formatMessage({
            defaultMessage: "Primary navigation",
            id: "navigation.primary",
        });
    });
</script>

<style lang="scss">
    @use "./SiteNavigation.scss";
</style>
